import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class TeamService {
  constructor(private _api: ApiService) {}

  getTeamsByOrganization(
    organizationId: string,
    page: number,
    perPage: number,
    sort = 'created',
    ascending = false,
    handler = '',
    query = '',
  ): Observable<any> {
    return this._api
      .get(
        `/organization/${organizationId}/teams?sort=${sort}&ascending=${ascending}&limit=${perPage}&offset=${page}${handler ? `&handler=${handler}` : ''}${query ? `&query=${query}` : ''}`,
      )
      .pipe(map((data) => data));
  }

  addTeamToProject(projectId: string, role: string, teamId: string): Observable<any> {
    return this._api.post(`/project/${projectId}/${role}/team`, teamId).pipe(map((data) => data));
  }

  getTeamsDetails(query: string, page: number, perPage: number, sort: string, ascending: boolean, handler = ''): Observable<any> {
    const results = this._api
      .get(
        `/team?sort=${sort}&ascending=${ascending}&query=${encodeURIComponent(query)}&limit=${perPage}&offset=${page}&field=${name}${handler ? `&handler=${handler}` : ''}`,
      )
      .pipe(map((data) => data));
    return results;
  }

  getOrganizationSpecificTeams(query: string): Observable<any> {
    const results = this._api.get(`/team?query=${encodeURIComponent(query)}`).pipe(map((data) => data));
    return results;
  }

  getTeamsByProjectId(projectId: string): Observable<any> {
    return this._api.get(`/project/${projectId}/teams`).pipe(map((data) => data));
  }

  getTeamById(id: string): Observable<any> {
    const results = this._api.get(`/team/${id}`).pipe(map((data) => data));
    return results;
  }

  addTeam(name: string, description: string, organizationId: string, uriLabel: string): Observable<any> {
    const results = this._api
      .post(`/team`, {
        name,
        description,
        organizationId,
        uriLabel,
      })
      .pipe(map((data) => data));
    return results;
  }

  addUserToTeam(teamId: string, userId: string, role: string): Observable<any> {
    const results = this._api.post(`/team/${teamId}/${role.toLowerCase()}`, userId).pipe(map((data) => data));
    return results;
  }

  removeUserFromTeam(team: any, userId: string) {
    let role: string;
    let owners: string[] = [];
    owners = team.owners;
    if (owners?.indexOf(userId) >= 0) {
      role = 'owner';
    } else {
      role = 'member';
    }

    const results = this._api.delete(`/team/${team?.id}/${role}/${userId}`).pipe(map((data) => data));

    return results;
  }

  deleteTeam(id: string, cascade: boolean): Observable<any> {
    const results = this._api.delete(`/team/${id}?cascade=${cascade}`).pipe(map((data) => data));
    return results;
  }

  deleteProjectTeam(projectId: string, role: string, teamId: string): Observable<any> {
    const results = this._api.delete(`/project/${projectId}/${role}/team/${teamId}`).pipe(map((data) => data));
    return results;
  }

  checkTeamDuplicate(organizationId: string, teamName?: string, uriLabel?: string, teamId?: string): Observable<any> {
    const tmId = teamId ? `&teamId=${teamId}` : null;
    const tmName = teamName ? `&teamName=${encodeURIComponent(teamName)}` : null;
    const tmUriLabel = uriLabel ? `&uriLabel=${uriLabel}` : null;

    const results = this._api
      .postWithoutAuth(`/team/check?${tmId ? tmId : ''}${tmName ? tmName : ''}${tmUriLabel ? tmUriLabel : ''}&organizationId=${organizationId}`, {})
      .pipe(map((data) => data));
    return results;
  }

  updateTeam(id: string, name: string, description: string, website: string, email: string, uriLabel: string): Observable<any> {
    const results = this._api
      .patch(`/team/${id}`, {
        id: id,
        name: name,
        description: description,
        website: website,
        email: email,
        uriLabel: uriLabel,
      })
      .pipe(map((data) => data));
    return results;
  }

  getTeamRole(team: any, data: any, isOrganization = true, teamName?: boolean) {
    if (isOrganization) {
      if (data?.owners?.includes(team.id)) {
        return teamName ? `${team.name} (owner)` : 'owner';
      } else if (data?.members?.includes(team.id)) {
        return teamName ? `${team.name} (member)` : 'member';
      }
    } else {
      if (data?.admins?.includes(team.id)) {
        return teamName ? `${team.name} (admin)` : 'admin';
      } else if (data?.writers?.includes(team.id)) {
        return teamName ? `${team.name} (writer)` : 'writer';
      } else if (data?.readers?.includes(team.id)) {
        return teamName ? `${team.name} (reader)` : 'reader';
      }
    }
    return teamName ? team.name : '';
  }

  changeTeamUserRole(teamId: string, currentRole: string, userId: string, newRole: string): Observable<any> {
    return this._api.put(`/team/${teamId}/${currentRole}/${userId}`, newRole).pipe(map((data) => data));
  }

  changeProjectTeamRole(projectId: string, teamId: string, currentRole: string, newRole: string) {
    return this._api.put(`/project/${projectId}/${currentRole}/team/${teamId}`, newRole).pipe(map((data) => data));
  }
}
